import React from 'react';
import { Modal, Box, Typography, Button, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

const ModalContainer = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '600px',
    backgroundColor: '#fafafa',
    border: '1px solid #ddd',
    boxShadow: 24,
    padding: theme.spacing(4),
    borderRadius: '12px',
    outline: 'none',
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
}));

/**
 * RegisterBusinesTermForm
 * - reuses the same ModalContainer / CloseButton styling
 * - shows Business responsibility clause (DE + EN)
 * - requires accept to continue (disable button until checked)
 */
export const RegisterBusinesTermForm = ({ open, handleClose, onAccept }) => {
    const [checked, setChecked] = React.useState(false);

    const handleAccept = () => {
        if (!checked) return;
        onAccept(true);
        handleClose?.();
    };

    const handleModalClose = () => {
        setChecked(false);
        handleClose?.();
    };

    return (
        <Modal
            open={!!open}
            onClose={handleModalClose}
            aria-labelledby="business-terms-title"
            aria-describedby="business-terms-description"
        >
            <ModalContainer>
                <CloseButton onClick={handleModalClose} aria-label="Close">
                    <CloseIcon />
                </CloseButton>

                <Typography
                    id="business-terms-title"
                    variant="h5"
                    component="h2"
                    gutterBottom
                    textAlign="center"
                >
                    Business Terms & Data Responsibility
                </Typography>

                <Typography
                    id="business-terms-description"
                    sx={{
                        mt: 2,
                        maxHeight: '320px',
                        overflowY: 'auto',
                        padding: 2,
                        lineHeight: 1.6,
                        fontSize: '0.95rem',
                        backgroundColor: '#ffffff',
                        borderRadius: '8px',
                    }}
                >
                    <strong>Verantwortlichkeit der Unternehmen (Nutzer der Plattform)</strong>
                    <br />
                    Die Plattform <strong>„Plus Appointment“</strong> stellt ausschließlich eine technische Softwarelösung zur
                    Verwaltung von Terminen, Dienstleistungen, Mitarbeitern und Kundeninformationen bereit.
                    <br />
                    <br />
                    Das jeweilige Unternehmen, das die Plattform nutzt, handelt als <strong>Verantwortlicher</strong> im Sinne von{' '}
                    <strong>Art. 4 Nr. 7 DSGVO</strong> und ist für die Rechtmäßigkeit der Verarbeitung verantwortlich.{' '}
                    <strong>Plus Appointment</strong> handelt ausschließlich als <strong>Auftragsverarbeiter</strong> gemäß{' '}
                    <strong>Art. 28 DSGVO</strong> (technischer Dienstleister).
                    <br />
                    <br />
                    Das Unternehmen ist allein verantwortlich für:
                    <ul>
                        <li>das Erstellen, Bearbeiten und Löschen von Terminen,</li>
                        <li>die Verwaltung von Kunden-, Mitarbeiter- und Servicedaten,</li>
                        <li>die Richtigkeit der eingegebenen Informationen,</li>
                        <li>die Kommunikation mit Kunden,</li>
                        <li>die Einhaltung der DSGVO und des österreichischen Datenschutzgesetzes (DSG).</li>
                    </ul>
                    <strong>Haftungsausschluss:</strong> Plus Appointment übernimmt keine Verantwortung für Inhalte, Terminfehler,
                    Ausfälle von Dienstleistungen oder Streitigkeiten zwischen Unternehmen und Kunden, soweit gesetzlich zulässig.
                    <br />
                    <br />
                    <hr />
                    <br />
                    <strong>Responsibility of Businesses (Platform Users)</strong>
                    <br />
                    <strong>Plus Appointment</strong> provides only a technical software platform to manage appointments, services,
                    staff, and customer information.
                    <br />
                    <br />
                    The business using the platform is the <strong>data controller</strong> within the meaning of{' '}
                    <strong>Article 4(7) GDPR</strong>. <strong>Plus Appointment</strong> acts solely as a{' '}
                    <strong>data processor</strong> under <strong>Article 28 GDPR</strong> (technical service provider).
                    <br />
                    <br />
                    The business is solely responsible for:
                    <ul>
                        <li>creating, editing, and deleting appointments,</li>
                        <li>managing customer, staff, and service data,</li>
                        <li>ensuring the accuracy of the information entered,</li>
                        <li>customer communication,</li>
                        <li>compliance with GDPR and Austrian data protection law (DSG).</li>
                    </ul>
                    <strong>Disclaimer:</strong> To the extent permitted by law, Plus Appointment is not responsible for bookings,
                    service delivery, data entered by businesses, or disputes between a business and its customers.
                </Typography>

                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                        id="acceptBusinessTerms"
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => setChecked(e.target.checked)}
                        style={{ width: 18, height: 18 }}
                    />
                    <label htmlFor="acceptBusinessTerms" style={{ fontSize: '0.95rem' }}>
                        I confirm that my business is responsible for appointments and customer/staff/service data.
                    </label>
                </Box>

                <Box display="flex" justifyContent="center" mt={2}>
                    <Button variant="contained" color="primary" onClick={handleAccept} disabled={!checked}>
                        Accept
                    </Button>
                </Box>
            </ModalContainer>
        </Modal>
    );
};